import React from "react";

import { Button, Icon, IconButton } from "../../components/ui";
import Window from "../../contexts/Window";
import { noAutofillProps } from "../../core/noAutofill";

import "./address-book.scss";

/**
 * Extra address sets typed while CREATING a user.
 *
 * The address book proper needs a user id to save against — every field there
 * auto-saves through `/users/{id}/addresses`. During creation there is no id
 * yet, so these rows are held in the parent Wizard's state and written straight
 * after the user is created.
 *
 * They are therefore the ONE place in the address UI that cannot auto-save.
 * Rather than imply otherwise, each row says so: the work is kept, and it is
 * stored the moment the user exists. Once the record is created this component
 * disappears and the auto-saving book takes over for good.
 *
 * The first address is NOT here — it travels with the user payload itself and
 * becomes `users.address_id`, the default.
 */
const BLANK = {
  label: "", line1: "", line2: "", locality: "", region: "", postal_code: "", country_id: "",
};

const PendingAddresses = ({ addresses = [], setAddresses, isRC }) => {
  if (!setAddresses) return null;

  const countries = Window.COUNTRIES || [];

  const add = () => setAddresses([...addresses, { ...BLANK }]);
  const remove = (i) => setAddresses(addresses.filter((_, n) => n !== i));
  const set = (i, k) => (e) => {
    const next = addresses.map((a, n) => (n === i ? { ...a, [k]: e.target.value } : a));
    setAddresses(next);
  };

  return (
    <div className="address-book address-book--pending">
      <div className="address-book__head">
        <div className="address-book__title">Additional addresses</div>
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<Icon name="Plus" size="sm" />}
          data-btn="address-add-pending"
          onClick={add}
          disabled={isRC}
        >
          Add address
        </Button>
      </div>

      {addresses.length === 0 ? (
        <div className="address-book__empty">
          The address above is the default. Add more sets here if you need them.
        </div>
      ) : (
        <ul className="address-book__list">
          {addresses.map((a, i) => (
            <li key={i} className="address-book__item address-book__item--new">
              <div className="address-book__body">
                <div className="address-form">
                  <div className="address-form__row">
                    <input
                      placeholder="Label (e.g. Office)"
                      value={a.label}
                      onChange={set(i, "label")}
                      {...noAutofillProps(`extra-label-${i}`)}
                    />
                    <select value={a.country_id} onChange={set(i, "country_id")}>
                      <option value="" disabled>— Country —</option>
                      {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="address-form__row">
                    <input placeholder="Address" value={a.line1} onChange={set(i, "line1")}
                      {...noAutofillProps(`extra-line1-${i}`)} />
                    <input placeholder="Address - line 2" value={a.line2} onChange={set(i, "line2")}
                      {...noAutofillProps(`extra-line2-${i}`)} />
                  </div>
                  <div className="address-form__row">
                    <input placeholder="Postal code" value={a.postal_code} onChange={set(i, "postal_code")}
                      {...noAutofillProps(`extra-postal-${i}`)} />
                    <input placeholder="City" value={a.locality} onChange={set(i, "locality")}
                      {...noAutofillProps(`extra-city-${i}`)} />
                  </div>
                  <div className="address-form__row">
                    <input placeholder="State / Region" value={a.region} onChange={set(i, "region")}
                      {...noAutofillProps(`extra-region-${i}`)} />
                    <span />
                  </div>
                  <div className="address-form__status">
                    {a.country_id
                      ? "Saved when you create the user"
                      : "Pick a country — it can't be saved without one"}
                  </div>
                </div>
              </div>
              <div className="address-book__actions">
                <IconButton
                  variant="ghost"
                  size="sm"
                  label="Remove"
                  title="Remove"
                  data-btn="address-delete-pending"
                  onClick={() => remove(i)}
                >
                  <Icon name="X" size="sm" />
                </IconButton>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PendingAddresses;
